import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  CardHeader,
  Alert,
  CircularProgress,
  DialogActions,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Badge,
  Fade,
  Zoom,
  TextField,
  InputAdornment
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Badge as BadgeIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader } from "../../components/CustomLoader";
import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { capitalize } from "../../utils/helpers";
import { IMAGE_BASE } from "../../utils/constants";
import UserActivityTracking from "./UserActivityTracking";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import BackButton from "../../components/Buttons/BackButton";

const ViewUserManagement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [showActivity, setShowActivity] = useState(false);
  const [kycDocuments, setKycDocuments] = useState([]);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycApproveLoading, setKycApproveLoading] = useState(false);
  const [kycRejectLoading, setKycRejectLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentDialog, setDocumentDialog] = useState(false);

  const userData = async () => {
    try {
      setLoading(true);
      const response = await userServices.getById({ id });
      setData(response.data.data);
      setKycDocuments(response.data?.data?.driverLicenseImage || []);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      userData();
    }
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleKycAction = async (action) => {
    try {
      if (action === 'approved') {
        setKycApproveLoading(true);
      } else if (action === 'rejected') {
        setKycRejectLoading(true);
      }
      
      const response = await userServices.kycStatus({
        id,
        kycStatus: action,
      });
      toast.success(response?.data?.message || `KYC ${action} successfully`);
      // Refresh user data locally for immediate view
      await userData();
    } catch (error) {
      errorHandler(error);
    } finally {
      if (action === 'approved') {
        setKycApproveLoading(false);
      } else if (action === 'rejected') {
        setKycRejectLoading(false);
      }
    }
  };

  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setDocumentDialog(true);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // Handle both string and object formats
      const docPath = typeof doc === 'string' ? doc : doc.fileName || doc.url || doc;
      const fileName = typeof doc === 'object' ? (doc.fileName || doc.name || `kyc-document-${Date.now()}`) : `kyc-document-${Date.now()}`;

      const response = await fetch(IMAGE_BASE + docPath);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);

    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: try direct download with proper headers
      try {
        const link = document.createElement('a');
        link.href = IMAGE_BASE + (typeof doc === 'string' ? doc : doc.fileName || doc.url || doc);
        link.download = typeof doc === 'object' ? (doc.fileName || doc.name || `kyc-document-${Date.now()}`) : `kyc-document-${Date.now()}`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (fallbackError) {
        console.error('Fallback download also failed:', fallbackError);
        // Final fallback: open in new tab
        window.open(IMAGE_BASE + (typeof doc === 'string' ? doc : doc.fileName || doc.url || doc), '_blank');
      }
    }
  };

  return (
    <CardLayout title="User Details">
      <Box sx={{ mb: 3 }}>
        

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Loader />
          </Box>
        ) : (
          <Box>
          <Box className="view-user-tabs-wrapper">
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="user details tabs"
              className="orange-tabs"
            >
              <Tab
                icon={<PersonIcon />}
                iconPosition="start"
                label="User Details"
              />
              {/* <Tab
                icon={<AssignmentIcon />}
                iconPosition="start"
                label="Activity Tracking"
              /> */}
              <Tab
                icon={<SecurityIcon />}
                iconPosition="start"
                label="KYC Verification"
              />
            </Tabs>
          </Box>

          <Box className="view-user-content-container">

            {tabValue === 0 && (
              <Fade in={tabValue === 0} timeout={300}>
                <Box>
                  {/* User Profile Header */}
                  <Card className="view-user-profile-card">
                    <Box className="view-user-profile-header">
                      <Box className="view-user-profile-avatar-container">
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            <Box className={`view-user-status-badge ${data?.isActive ? 'active' : 'inactive'}`}>
                              <Box className="view-user-status-badge-dot" />
                            </Box>
                          }
                        >
                          <Avatar
                            alt={`${data?.firstName} ${data?.lastName}`}
                            src={data?.userImage ? IMAGE_BASE + data.userImage : undefined}
                            className="view-user-profile-avatar"
                          >
                            <PersonIcon className="view-user-profile-avatar-large" />
                          </Avatar>
                        </Badge>

                        <Box className="view-user-profile-info">
                          <Typography variant="h4" className="view-user-profile-name">
                            {capitalize(data?.firstName + " " + data?.lastName)}
                          </Typography>
                          <Typography variant="h6" className="view-user-profile-email">
                            {data?.email}
                          </Typography>

                          <Stack direction="row" spacing={2} flexWrap="wrap" className="view-user-profile-chips">
                            <Chip
                              label={data?.role === "buyer" ? "Buyer" : data?.role === "seller" ? "Seller" : capitalize(data?.role)}
                              className="view-user-profile-chip"
                            />
                            {/* <Chip
                              label={
                                data?.kycStatus === "approved" ? "Verified" :
                                  data?.kycStatus === "rejected" ? "Rejected" :
                                    data?.kycStatus === "pending" ? "Pending" :
                                      "Not Submitted"
                              }
                              className="view-user-profile-chip"
                            /> */}
                            <Chip
                              label={data?.isBlocked ? "Blocked" : data?.status ? "Active" : "Inactive"}
                              className="view-user-profile-chip"
                            />
                          </Stack>
                        </Box>
                      </Box>
                    </Box>
                  </Card>

                  {/* Business Information (Dealer) - shown before Personal Info */}
                  {data?.role === "dealer" && (
                    <Card className="view-user-info-card-simple">
                      <CardHeader
                        title={
                          <Box className="view-user-info-header-simple">
                            <BusinessIcon className="view-user-icon-large view-user-icon-orange" />
                            <Typography variant="h6" className="orange-section-title">
                              Business Information
                            </Typography>
                          </Box>
                        }
                        className="view-user-card-header"
                      />
                      <CardContent>
                        <Grid container spacing={3}>
                          {data?.businessName && (
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Business Name"
                                value={data.businessName}
                                InputProps={{
                                  readOnly: true,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <BusinessIcon className="view-user-icon-orange" />
                                    </InputAdornment>
                                  ),
                                }}
                                className="orange-input-field-special"
                              />
                            </Grid>
                          )}
                          {data?.businessAddress && (
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Business Address"
                                value={data.businessAddress}
                                InputProps={{
                                  readOnly: true,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <LocationIcon className="view-user-icon-gray" />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#F9FAFB',
                                    '& fieldset': { borderColor: '#E5E7EB' },
                                    '&:hover fieldset': { borderColor: '#F59E0B' },
                                    '&.Mui-focused fieldset': { borderColor: '#F59E0B' },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: '#6B7280',
                                    '&.Mui-focused': { color: '#F59E0B' },
                                  },
                                }}
                              />
                            </Grid>
                          )}
                          {data?.dealershipNumber && (
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Dealership Number"
                                value={data.dealershipNumber}
                                InputProps={{
                                  readOnly: true,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <BadgeIcon className="view-user-icon-gray" />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#F9FAFB',
                                    '& fieldset': { borderColor: '#E5E7EB' },
                                    '&:hover fieldset': { borderColor: '#F59E0B' },
                                    '&.Mui-focused fieldset': { borderColor: '#F59E0B' },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: '#6B7280',
                                    '&.Mui-focused': { color: '#F59E0B' },
                                  },
                                }}
                              />
                            </Grid>
                          )}
                          {data?.taxNumber && (
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Tax Number"
                                value={data.taxNumber}
                                InputProps={{
                                  readOnly: true,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <BadgeIcon className="view-user-icon-gray" />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#F9FAFB',
                                    '& fieldset': { borderColor: '#E5E7EB' },
                                    '&:hover fieldset': { borderColor: '#F59E0B' },
                                    '&.Mui-focused fieldset': { borderColor: '#F59E0B' },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: '#6B7280',
                                    '&.Mui-focused': { color: '#F59E0B' },
                                  },
                                }}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Personal Information */}
                  <Card className="view-user-info-card">
                    <CardHeader
                      title={
                        <Box className="view-user-info-header">
                          <PersonIcon className="view-user-info-icon" />
                          <Typography variant="h6" className="orange-section-title">
                            Personal Information
                          </Typography>
                        </Box>
                      }
                      className="view-user-card-header"
                    />
                    <CardContent className="view-user-info-content">
                      <Grid container spacing={3} className="view-user-info-grid">
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Email"
                            value={data?.email || ""}
                            className="orange-input-field"
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon className="view-user-icon-gray" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        {data?.phoneNumber && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Phone"
                              value={data?.phoneNumber || ""}
                              className="orange-input-field"
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PhoneIcon className="view-user-icon-gray" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        )}

                        {data?.dob && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Date of Birth"
                              value={data?.dob ? data.dob.split("T")[0] : ""}
                              className="orange-input-field"
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <CalendarIcon className="view-user-icon-gray" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        )}

                        {/* {data?.gender && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Gender"
                              value={data?.gender || ""}
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonIcon className="view-user-icon-gray" />
                                  </InputAdornment>
                                ),
                              }}
                              className="orange-input-field"
                            />
                          </Grid>
                        )} */}

                        {data?.driverLicence && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Driver License"
                              value={data?.driverLicence || ""}
                              className="orange-input-field-special"
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <BadgeIcon className="view-user-icon-orange" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        )}

                        {data?.createdAt && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Registration Date"
                              value={data?.createdAt ? new Date(data.createdAt).toLocaleDateString() : ""}
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <CalendarIcon className="view-user-icon-gray" />
                                  </InputAdornment>
                                ),
                              }}
                              className="orange-input-field"
                            />
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Address Information */}
                  <Card className="view-user-info-card-simple">
                    <CardHeader
                      title={
                        <Box className="view-user-info-header-simple">
                          <LocationIcon className="view-user-icon-large view-user-icon-orange" />
                          <Typography variant="h6" className="orange-section-title">
                            Address Information
                          </Typography>
                        </Box>
                      }
                      className="view-user-card-header"
                    />
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Street Address"
                            value={data?.address || ""}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationIcon className="view-user-icon-gray" />
                                </InputAdornment>
                              ),
                            }}
                            className="orange-input-field"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="City"
                            value={data?.city || ""}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationIcon className="view-user-icon-gray" />
                                </InputAdornment>
                              ),
                            }}
                            className="orange-input-field"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Province/State"
                            value={data?.province || ""}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationIcon className="view-user-icon-gray" />
                                </InputAdornment>
                              ),
                            }}
                            className="orange-input-field"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Country"
                            value={data?.country || ""}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationIcon className="view-user-icon-gray" />
                                </InputAdornment>
                              ),
                            }}
                            className="orange-input-field"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Postal Code"
                            value={data?.postalCode || ""}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationIcon className="view-user-icon-gray" />
                                </InputAdornment>
                              ),
                            }}
                            className="orange-input-field"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              </Fade>
            )}

            {/* {tabValue === 1 && (
              <Box>
                <UserActivityTracking
                  userId={id}
                  show={true}
                  onHide={() => setShowActivity(false)}
                  data={data}
                />
              </Box>
            )} */}

            {tabValue === 1 && (
              <Fade in={tabValue === 1} timeout={300}>
                <Box>
                  {/* KYC Status Header */}
                  <Card className="view-user-kyc-card">
                    <Box className={`view-user-kyc-header ${data?.kycStatus || 'not-submitted'}`}>
                      <Box className="view-user-kyc-container">
                        <Box className="view-user-kyc-left">
                          <Box className="view-user-kyc-icon-container">
                            <SecurityIcon className="view-user-kyc-icon" />
                          </Box>
                          <Box>
                            <Typography variant="h4" className="view-user-kyc-title">
                              KYC Verification
                            </Typography>
                            <Typography variant="h6" className="view-user-kyc-subtitle">
                              Identity verification status
                            </Typography>
                            <Chip
                              label={
                                data?.kycStatus === "approved" ? "Verified" :
                                  data?.kycStatus === "rejected" ? "Rejected" :
                                    data?.kycStatus === "pending" ? "Pending Review" :
                                      "Not Submitted"
                              }
                              className="view-user-kyc-status-chip"
                            />
                          </Box>
                        </Box>
                      </Box>
                      {/* Decorative elements */}
                      <Box className="view-user-kyc-decorative-1" />
                      <Box className="view-user-kyc-decorative-2" />
                    </Box>
                  </Card>

                  {/* KYC Documents Section */}
                  <Card className="view-user-kyc-documents-card">
                    <CardHeader
                      title={
                        <Box className="view-user-kyc-documents-header">
                          <AssignmentIcon className="view-user-kyc-documents-icon" />
                          <Typography variant="h6" className="orange-section-title">
                            Submitted Documents
                          </Typography>
                          <Chip
                            label={`${kycDocuments.length} documents`}
                            className="view-user-kyc-documents-count"
                            size="small"
                          />
                        </Box>
                      }
                      className="view-user-card-header"
                    />
                    <CardContent className="view-user-kyc-documents-content">
                      {kycLoading ? (
                        <Box className="view-user-kyc-loading-container">
                          <Box textAlign="center">
                            <CircularProgress size={40} />
                            <Typography variant="body2" color="text.secondary" className="view-user-kyc-loading-text">
                              Loading documents...
                            </Typography>
                          </Box>
                        </Box>
                      ) : kycDocuments.length === 0 ? (
                        <Box className="view-user-kyc-empty-state">
                          <SecurityIcon className="view-user-kyc-empty-icon" />
                          <Typography variant="h6" className="view-user-kyc-empty-title">
                            No Documents Submitted
                          </Typography>
                          <Typography variant="body2" className="view-user-kyc-empty-subtitle">
                            This user has not submitted any KYC documents yet.
                          </Typography>
                        </Box>
                      ) : (
                        <Grid container spacing={3} className="view-user-kyc-documents-grid">
                          {kycDocuments.map((doc, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Zoom in={true} timeout={300 + (index * 100)}>
                                <Card className="view-user-kyc-document-card">
                                  <Box className="view-user-kyc-document-relative">
                                    <CardMedia
                                      component="img"
                                      height="250"
                                      image={IMAGE_BASE + doc}
                                      alt={doc.documentType || 'KYC Document'}
                                      className="view-user-kyc-document-image"
                                      onClick={() => handleViewDocument(doc)}
                                    />
                                    {/* Action buttons overlay */}
                                    <Box className="view-user-kyc-document-actions">
                                      {/* View button */}
                                      <Tooltip title="View Document">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewDocument(doc);
                                          }}
                                          className="view-user-kyc-document-action-button"
                                        >
                                          <ViewIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>

                                      {/* Download button */}
                                      <Tooltip title="Download Document">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDownloadDocument(doc);
                                          }}
                                          className="view-user-kyc-document-action-button"
                                        >
                                          <DownloadIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>

                                  <CardContent className="view-user-kyc-document-content">
                                    <Typography variant="body2" color="text.secondary" className="view-user-kyc-document-text">
                                      Document #{index + 1}
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                      {doc.documentType || 'KYC Document'}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Zoom>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </CardContent>
                  </Card>
                  {/* KYC Action Buttons */}
                 
                  {kycDocuments.length > 0 && data?.kycStatus !== 'not_submitted' && (
                    <Card className="view-user-kyc-actions-card">
                      <CardContent className="view-user-kyc-actions-content">
                        <Box className="view-user-kyc-actions-header">
                          <Box className="view-user-kyc-actions-icon">
                            <SecurityIcon className="view-user-icon-medium view-user-icon-white" />
                          </Box>
                          <Typography variant="h6" className="orange-section-title">
                            KYC Review Required
                          </Typography>
                        </Box>
                        <Typography variant="body2" className="view-user-kyc-actions-description">
                          This user has submitted KYC documents and is awaiting your review. Please carefully examine the documents before making a decision.
                        </Typography>
                        <Box className="view-user-kyc-actions-buttons">
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<RejectIcon />}
                            onClick={() => handleKycAction('rejected')}
                            disabled={ data?.kycStatus === 'rejected' || kycRejectLoading || kycApproveLoading}
                            className="view-user-kyc-reject-button"
                          >
                            {kycRejectLoading ? <CircularProgress size={20} /> : 'Reject KYC'}
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<ApproveIcon />}
                            onClick={() => handleKycAction('approved')}
                            disabled={ data?.kycStatus === 'approved' || kycApproveLoading || kycRejectLoading}
                            className="view-user-kyc-approve-button"
                          >
                            {kycApproveLoading ? <CircularProgress size={20} /> : 'Approve KYC'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Fade>
            )}
          </Box>
          </Box>
        )}
      </Box>

      {/* Document Viewer Dialog */}
      <Dialog
        open={documentDialog}
        onClose={() => setDocumentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedDocument?.documentType || 'KYC Document'}
        </DialogTitle>
        <DialogContent className="view-user-document-viewer-content">
          {selectedDocument && (
            <Box className="view-user-document-viewer-image-container">
              <img
                src={IMAGE_BASE + (typeof selectedDocument === 'string' ? selectedDocument : selectedDocument.fileName || selectedDocument.url || selectedDocument)}
                alt={typeof selectedDocument === 'object' ? (selectedDocument.documentType || 'KYC Document') : 'KYC Document'}
                className="view-user-document-viewer-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <Box className="view-user-document-viewer-error" style={{ display: 'none' }}>
                <Typography variant="h6">Unable to load document</Typography>
                <Typography variant="body2">The document may be corrupted or in an unsupported format</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentDialog(false)}>
            Close
          </Button>
          {selectedDocument && (
            <Button
              onClick={() => handleDownloadDocument(selectedDocument)}
              startIcon={<DownloadIcon />}
              variant="contained"
              className="orange-download-button"
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </CardLayout>
  );
};

export default ViewUserManagement;
